import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import DataTable from '../components/DataTable';
import IconButton from '../components/IconButton';
import './MaintenancePlans.css';

function MaintenancePlans({ tenantId }) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [plans, setPlans] = useState([]);
  const [serviceTemplates, setServiceTemplates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    intervals: [],
    serviceTemplateIds: [],
    active: true
  });

  useEffect(() => {
    if (tenantId) {
      loadPlans();
      loadServiceTemplates();
    }
  }, [tenantId]);

  const loadPlans = async () => {
    try {
      const data = await api.get('/maintenance-plans', tenantId);
      setPlans(data);
    } catch (error) {
      console.error('Error loading maintenance plans:', error);
    }
  };

  const loadServiceTemplates = async () => {
    try {
      const data = await api.get('/service-templates', tenantId);
      setServiceTemplates(data);
    } catch (error) {
      console.error('Error loading service templates:', error);
    }
  };

  const handleAddInterval = () => {
    setFormData({
      ...formData,
      intervals: [...formData.intervals, { type: 'mileage', value: '', unit: 'km', id: `interval-${Date.now()}` }]
    });
  };

  const handleRemoveInterval = (index) => {
    setFormData({
      ...formData,
      intervals: formData.intervals.filter((_, i) => i !== index)
    });
  };

  const handleIntervalChange = (index, field, value) => {
    const newIntervals = [...formData.intervals];
    newIntervals[index] = { ...newIntervals[index], [field]: value };
    setFormData({ ...formData, intervals: newIntervals });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        intervals: formData.intervals.map(interval => ({
          type: interval.type,
          value: parseInt(interval.value) || 0,
          unit: interval.unit,
          id: interval.id
        }))
      };

      if (editingPlan) {
        await api.put(`/maintenance-plans/${editingPlan.id}`, submitData, tenantId);
      } else {
        await api.post('/maintenance-plans', submitData, tenantId);
      }
      setShowForm(false);
      setEditingPlan(null);
      setFormData({
        name: '',
        description: '',
        intervals: [],
        serviceTemplateIds: [],
        active: true
      });
      loadPlans();
    } catch (error) {
      console.error('Error saving maintenance plan:', error);
      showNotification('Fehler beim Speichern des Wartungsplans', 'error');
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name || '',
      description: plan.description || '',
      intervals: plan.intervals || [],
      serviceTemplateIds: plan.serviceTemplateIds || [],
      active: plan.active !== undefined ? plan.active : true
    });
    setShowForm(true);
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Wartungsplan wirklich löschen?')) return;

    try {
      await api.delete(`/maintenance-plans/${planId}`, tenantId);
      loadPlans();
    } catch (error) {
      console.error('Error deleting maintenance plan:', error);
      showNotification('Fehler beim Löschen des Wartungsplans', 'error');
    }
  };

  const formatInterval = (interval) => {
    if (interval.type === 'mileage' && interval.unit === 'km') {
      return `Alle ${interval.value.toLocaleString('de-DE')} km`;
    } else if (interval.type === 'time' && interval.unit === 'months') {
      return `Alle ${interval.value} Monate`;
    }
    return '-';
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      sortType: 'string',
      accessor: (row) => row.name || '',
      render: (row) => (
        <button
          className="link-button"
          onClick={() => handleEdit(row)}
        >
          {row.name}
        </button>
      )
    },
    {
      key: 'description',
      header: 'Beschreibung',
      sortType: 'string',
      accessor: (row) => row.description || ''
    },
    {
      key: 'intervals',
      header: 'Intervalle',
      sortType: 'string',
      accessor: (row) => (row.intervals || []).map(i => formatInterval(i)).join(', '),
      render: (row) => (
        <div>
          {(row.intervals || []).map((interval, idx) => (
            <span key={idx} style={{ display: 'inline-block', marginRight: '0.5rem', marginBottom: '0.25rem', padding: '0.25rem 0.5rem', backgroundColor: '#f0f0f0', borderRadius: '4px', fontSize: '0.85em' }}>
              {formatInterval(interval)}
            </span>
          ))}
        </div>
      )
    },
    {
      key: 'serviceTemplates',
      header: 'Standardreparaturen',
      sortType: 'string',
      accessor: (row) => (row.serviceTemplateIds || []).length,
      render: (row) => {
        const templates = (row.serviceTemplateIds || []).map(id => 
          serviceTemplates.find(t => t.id === id)
        ).filter(Boolean);
        return (
          <div>
            {templates.length > 0 ? (
              templates.map((template, idx) => (
                <span key={idx} style={{ display: 'inline-block', marginRight: '0.5rem', marginBottom: '0.25rem', padding: '0.25rem 0.5rem', backgroundColor: '#e3f2fd', borderRadius: '4px', fontSize: '0.85em' }}>
                  {template.name}
                </span>
              ))
            ) : (
              <span style={{ color: '#999' }}>-</span>
            )}
          </div>
        );
      }
    },
    {
      key: 'active',
      header: 'Aktiv',
      sortType: 'string',
      accessor: (row) => row.active ? 'Ja' : 'Nein',
      render: (row) => (
        <span style={{ color: row.active ? '#4caf50' : '#999' }}>
          {row.active ? '✓' : '✗'}
        </span>
      )
    },
    {
      key: 'actions',
      header: 'Aktionen',
      sortType: 'none',
      accessor: () => '',
      render: (row) => (
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          <IconButton
            icon="edit"
            onClick={() => handleEdit(row)}
            title="Bearbeiten"
          />
          <IconButton
            icon="delete"
            onClick={() => handleDelete(row.id)}
            title="Löschen"
            className="danger"
          />
        </div>
      )
    }
  ];

  return (
    <div className="maintenance-plans">
      <div className="page-header">
        <h1>Wartungspläne</h1>
        <button onClick={() => setShowForm(true)} className="btn-primary">
          Neuer Wartungsplan
        </button>
      </div>

      {showForm && (
        <div className="form-overlay">
          <div className="form-container">
            <h2>{editingPlan ? 'Wartungsplan bearbeiten' : 'Neuer Wartungsplan'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Beschreibung</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Intervalle *</label>
                <p className="form-hint">Definieren Sie, wann Wartungen durchgeführt werden sollen</p>
                {formData.intervals.map((interval, index) => (
                  <div key={interval.id || index} style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9em' }}>Typ</label>
                        <select
                          value={interval.type}
                          onChange={(e) => handleIntervalChange(index, 'type', e.target.value)}
                          style={{ width: '100%', padding: '0.5rem' }}
                        >
                          <option value="mileage">Kilometerstand</option>
                          <option value="time">Zeit</option>
                        </select>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9em' }}>Wert *</label>
                        <input
                          type="number"
                          value={interval.value}
                          onChange={(e) => handleIntervalChange(index, 'value', e.target.value)}
                          placeholder={interval.type === 'mileage' ? 'z.B. 15000' : 'z.B. 12'}
                          required
                          style={{ width: '100%', padding: '0.5rem' }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9em' }}>Einheit</label>
                        <select
                          value={interval.unit}
                          onChange={(e) => handleIntervalChange(index, 'unit', e.target.value)}
                          style={{ width: '100%', padding: '0.5rem' }}
                        >
                          {interval.type === 'mileage' ? (
                            <option value="km">km</option>
                          ) : (
                            <option value="months">Monate</option>
                          )}
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveInterval(index)}
                        className="btn-small btn-danger"
                      >
                        Entfernen
                      </button>
                    </div>
                  </div>
                ))}
                <button type="button" onClick={handleAddInterval} className="btn-secondary btn-small">
                  Intervall hinzufügen
                </button>
              </div>

              <div className="form-group">
                <label>Standardreparaturen</label>
                <p className="form-hint">Diese Reparaturen werden automatisch bei geplanten Wartungen verwendet</p>
                <select
                  multiple
                  value={formData.serviceTemplateIds}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    setFormData({ ...formData, serviceTemplateIds: selected });
                  }}
                  style={{ minHeight: '150px', width: '100%' }}
                >
                  {serviceTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
                <p className="form-hint">Halten Sie Strg/Cmd gedrückt, um mehrere auszuwählen</p>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  />
                  Aktiv
                </label>
                <p className="form-hint">Nur aktive Pläne werden für automatische Wartungen verwendet</p>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingPlan ? 'Speichern' : 'Erstellen'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingPlan(null);
                    setFormData({
                      name: '',
                      description: '',
                      intervals: [],
                      serviceTemplateIds: [],
                      active: true
                    });
                  }}
                  className="btn-secondary"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DataTable data={plans} columns={columns} />
    </div>
  );
}

export default MaintenancePlans;

